import React from 'react';
import { useDispatch } from 'react-redux';
import { Helmet } from 'react-helmet';
import { Link, useNavigate } from 'react-router-dom';
import { Grid, CircularProgress, Button, ButtonGroup, Alert } from '@mui/material';
import _ from 'lodash';

import { useLazyQuery, useQuery, useMutation } from '@apollo/client';
import i18next from 'i18next';
import AddIcon from '@mui/icons-material/Add';
import DownloadIcon from '@mui/icons-material/Download';
import UploadIcon from '@mui/icons-material/Upload';

import PagedTable from 'components/table/PagedTable';
import { FormatDateTime } from 'components/DateTime';
import FileUploadButton from 'components/FileUploadButton';
import { dispatchException, dispatchMessage } from 'helper/snackbar';
import { exportToFile } from 'helper/download';
import { filterSelector } from 'helper/filter';
import { userSelector, hasAnyAdminSpace } from 'helper/security';

import { EMAILTEMPLATES_LIST_QUERY, EMAILTEMPLATES_COUNT_QUERY, EXPORT_EMAILTEMPLATE_MUTATION, IMPORT_EMAILTEMPLATE_MUTATION, REFETCH_EMAILTEMPLATES_QUERIES } from '../gql';
import { EContentEmailTemplate } from '__generated__/graphql';

import { EmailTemplateCopyButton, EmailTemplateDeleteButton } from './emailtemplate';
import { LanguageName } from 'components/Language';

/**
 * Email templates list page with pagination and filtering by space.
 * Supports copy and delete actions for email templates.
 */
export default function EmailTemplatesList() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const filter = filterSelector();
  const user = userSelector()!;

  const [queryFn, { loading, error, data }] = useLazyQuery(EMAILTEMPLATES_LIST_QUERY);
  const { data: countData } = useQuery(EMAILTEMPLATES_COUNT_QUERY, {
    variables: { spaceId: (filter && filter.spaceId) || null },
  });

  const refetchPage = (skip: number, take: number) => {
    queryFn({
      variables: {
        spaceId: (filter && filter.spaceId) || null,
        skip,
        take,
      },
      fetchPolicy: 'network-only',
    });
  };
  const [importMutationFunction, { loading: importLoading }] = useMutation(IMPORT_EMAILTEMPLATE_MUTATION);
  const [exportMutateFunction, { loading: exportLoading }] = useMutation(EXPORT_EMAILTEMPLATE_MUTATION);

  const getReceiver = (templateUsage: EContentEmailTemplate) => {
    const templateUsageString = templateUsage.toString();
    if (templateUsageString.includes('HOTEL')) {
      return i18next.t('emailtemplates-list-receiver-hotel');
    } else if (templateUsageString.includes('CLIENT')) {
      return i18next.t('emailtemplates-list-receiver-client');
    } else if (templateUsageString.includes('SEMINARGO')) {
      return i18next.t('emailtemplates-list-receiver-seminargo');
    } else if (templateUsageString.includes('USER')) {
      return i18next.t('emailtemplates-list-receiver-user');
    } else if (templateUsageString === 'RESET_PASSWORD') {
      return i18next.t('emailtemplates-list-receiver-user');
    }
    return null;
  };

  const getMissingTemplates = () => {
    const allTemplates = Object.keys(EContentEmailTemplate).filter(t => !t.includes('LISTER'));
    return allTemplates.filter(t => !data?.listEmailTemplates.some(et => et.usage === t));
  };

  const importToSpaceId = (filter && filter.spaceId) || user.space?.id;

  return (
    <>
      <Helmet>
        <title>{i18next.t('emailtemplates-list-page-title')}</title>
      </Helmet>
      <Grid container spacing={3}>
        {data && getMissingTemplates().length > 0 && (
          <Grid item xs={12}>
            <Alert severity="warning">
              {i18next.t('emailtemplates-list-warning-new-templates')} <br />
              <br />
              {i18next.t('emailtemplates-list-warning-missing-templates')}:{' '}
              {getMissingTemplates().map(t => (
                <li key={t}>{i18next.t('enums-EContentEmailTemplate-' + t)}</li>
              ))}
            </Alert>
          </Grid>
        )}
        <Grid item xs={12}>
          <PagedTable
            headers={[
              i18next.t('emailtemplates-list-header-usage'),
              i18next.t('emailtemplates-list-header-receiver'),
              i18next.t('emailtemplates-list-header-language'),
              i18next.t('emailtemplates-list-header-subject'),
              ...(user.isSingleSpace ? [] : [i18next.t('list-header-space')]),
              i18next.t('emailtemplates-list-header-updatedat'),
              '',
            ]}
            rows={
              data
                ? _.sortBy(data.listEmailTemplates, [l => i18next.t('enums-EContentEmailTemplate-' + l.usage), 'language'])
                    .filter(t => !t.usage.includes('LISTER'))
                    .map(row => [
                      <Link to={`/content/templates/emailtemplates/${row.id}`}>{i18next.t('enums-EContentEmailTemplate-' + row.usage)}</Link>,
                      getReceiver(row.usage),
                      <LanguageName language={row.language} />,
                      row.subject,
                      ...(user.isSingleSpace ? [] : [row.space.name]),
                      <FormatDateTime date={row.updatedAt} />,
                      <ButtonGroup>
                        <EmailTemplateCopyButton id={row.id} spaceId={row.space.id} icon />
                        <EmailTemplateDeleteButton id={row.id} spaceId={row.space.id} icon />
                      </ButtonGroup>,
                    ])
                : []
            }
            rowsLoading={loading}
            rowsErr={error}
            refetchPage={refetchPage}
            filterKey={filter?.spaceId}
            defaultPageSize={10}
            totalCount={countData?.countEmailTemplates}
          />
        </Grid>
        <Grid item xs={12}>
          {hasAnyAdminSpace(user) && (
            <Button
              sx={{ marginRight: 2 }}
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate('/content/templates/emailtemplates/_create')}
            >
              {i18next.t('emailtemplates-list-add')}
            </Button>
          )}
          <Button
            sx={{ marginRight: 2 }}
            variant="contained"
            color="secondary"
            startIcon={exportLoading ? <CircularProgress size={24} /> : <DownloadIcon />}
            disabled={exportLoading}
            onClick={async () => {
              try {
                const res = await exportMutateFunction({
                  variables: {
                    spaceId: (filter && filter.spaceId) || null,
                  },
                });
                if (res.data?.exportEmailTemplates) {
                  exportToFile(res.data?.exportEmailTemplates, 'application/json', 'seminargo-emailtemplates.json');
                }
              } catch (err) {
                dispatchException(dispatch, err);
              }
            }}
          >
            {i18next.t('emailtemplates-export-run')}
          </Button>
          {user.isAdmin && importToSpaceId && (
            <FileUploadButton
              name="importEmailTemplate"
              accept="application/json"
              disabled={importLoading}
              binary={() => false}
              startIcon={importLoading ? <CircularProgress size={24} /> : <UploadIcon />}
              onChange={async (name, type, content) => {
                try {
                  const res = await importMutationFunction({
                    variables: {
                      spaceId: importToSpaceId,
                      jsonText: content,
                    },
                    awaitRefetchQueries: true,
                    refetchQueries: REFETCH_EMAILTEMPLATES_QUERIES(),
                  });
                  dispatchMessage(dispatch, i18next.t('emailtemplates-import-ready'));
                } catch (err) {
                  dispatchException(dispatch, err);
                }
              }}
            >
              {i18next.t('emailtemplates-import-run')}
            </FileUploadButton>
          )}
        </Grid>
      </Grid>
    </>
  );
}
